import React, {useCallback, useMemo, useState} from 'react';
import PageButton from '../elements/PageButton/PageButton';
import PasswordInput from './PasswordInput';
import {API_BASE} from '../../utils/apiConfig';
import {PASSWORD_REQUIREMENTS_HINT, validatePassword} from '../../configs/passwordPolicy';
import './EmailAuthForm.css';

/**
 * Вход и регистрация по почте.
 *
 * Шаги идут по одному, а не одной формой: сначала адрес, и только потом пароль —
 * потому что до ответа сервера мы не знаем, вход это или регистрация, а тексты
 * и требования у них разные.
 *
 *   email    — ввод адреса, сервер отвечает, есть ли такой аккаунт
 *   login    — пароль существующего аккаунта, успех = вход
 *   register — пароль нового аккаунта, успех = отправленное письмо
 *   sent     — «письмо отправлено», аккаунт появится после перехода по ссылке
 *   forgot-sent — то же для забытого пароля: письмо со ссылкой смены
 */
const STEPS = {
  EMAIL: 'email',
  LOGIN: 'login',
  REGISTER: 'register',
  SENT: 'sent',
  FORGOT_SENT: 'forgot-sent',
};

const postJson = async (path, body) => {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return {ok: response.ok, data};
};

function EmailAuthForm({onAuthSuccess, onCancel, pageId = 'YandexAuthPage'}) {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  // Пароль проверяется и здесь, и на сервере. Здесь — чтобы не гонять человека
  // на сервер за очевидной ошибкой; сервер — потому что верить клиенту нельзя
  const passwordCheck = useMemo(
    () => validatePassword(password, {email}),
    [password, email]
  );

  const submitEmail = useCallback(async (event) => {
    event.preventDefault();
    setError(null);
    setIsBusy(true);
    try {
      const {ok, data} = await postJson('/auth/email/begin', {email});
      if (!ok) {
        setError(data.error || 'Не удалось проверить адрес');
        return;
      }
      setPassword('');
      setStep(data.exists ? STEPS.LOGIN : STEPS.REGISTER);
    } catch (requestError) {
      console.error('Email begin error', requestError);
      setError('Нет связи с сервером');
    } finally {
      setIsBusy(false);
    }
  }, [email]);

  const submitLogin = useCallback(async (event) => {
    event.preventDefault();
    setError(null);
    setIsBusy(true);
    try {
      const {ok, data} = await postJson('/auth/email/login', {email, password});
      if (!ok) {
        setError(data.error || 'Не удалось войти');
        return;
      }
      onAuthSuccess(data);
    } catch (requestError) {
      console.error('Email login error', requestError);
      setError('Нет связи с сервером');
    } finally {
      setIsBusy(false);
    }
  }, [email, password, onAuthSuccess]);

  const submitRegister = useCallback(async (event) => {
    event.preventDefault();
    if (!passwordCheck.isValid) {
      setError(passwordCheck.error);
      return;
    }
    setError(null);
    setIsBusy(true);
    try {
      const {ok, data} = await postJson('/auth/email/register', {email, password});
      if (!ok) {
        setError(data.error || 'Не удалось зарегистрировать');
        return;
      }
      setPassword('');
      setStep(STEPS.SENT);
    } catch (requestError) {
      console.error('Email register error', requestError);
      setError('Нет связи с сервером');
    } finally {
      setIsBusy(false);
    }
  }, [email, password, passwordCheck]);

  const requestPasswordReset = useCallback(async () => {
    setError(null);
    setIsBusy(true);
    try {
      const {ok, data} = await postJson('/auth/email/forgot', {email});
      if (!ok) {
        setError(data.error || 'Не удалось отправить письмо');
        return;
      }
      setPassword('');
      setStep(STEPS.FORGOT_SENT);
    } catch (requestError) {
      console.error('Password forgot error', requestError);
      setError('Нет связи с сервером');
    } finally {
      setIsBusy(false);
    }
  }, [email]);

  const goBackToEmail = useCallback(() => {
    setPassword('');
    setError(null);
    setStep(STEPS.EMAIL);
  }, []);

  if (step === STEPS.SENT) {
    return (
      <div className="email-auth-form">
        <h2 className="email-auth-title">Проверь почту</h2>
        <p className="email-auth-text">
          Мы отправили письмо на <b className={'text-coins'}>{email}</b>.
          <br/>Открой ссылку из письма — и аккаунт будет создан.
        </p>
        <p className="email-auth-hint">
          Письма нет? Загляни в «Спам».
          <br/>Ссылка действует сутки.
        </p>
        <PageButton
          type="button"
          onClick={onCancel}
          pageId={pageId}
          buttonId="email-sent-back"
        >
          Вернуться
        </PageButton>
      </div>
    );
  }

  if (step === STEPS.FORGOT_SENT) {
    return (
      <div className="email-auth-form">
        <h2 className="email-auth-title">Письмо отправлено</h2>
        <p className="email-auth-text">
          Если аккаунт с адресом <b>{email}</b> существует, мы отправили на него ссылку
          для смены пароля.
        </p>
        <p className="email-auth-hint">Ссылка действует час. Проверь и папку «Спам».</p>
        <PageButton
          type="button"
          onClick={onCancel}
          pageId={pageId}
          buttonId="forgot-sent-back"
        >
          Вернуться
        </PageButton>
      </div>
    );
  }

  if (step === STEPS.EMAIL) {
    return (
      <form className="email-auth-form" onSubmit={submitEmail}>
        <h2 className="email-auth-title">Вход по почте</h2>
        <input
          className="email-auth-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Почта"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={isBusy}
          autoFocus
        />
        {error && <p className="email-auth-error">{error}</p>}
        <PageButton
          type="submit"
          disabled={isBusy || !email.trim()}
          pageId={pageId}
          buttonId="email-continue"
        >
          {isBusy ? 'Проверяем...' : 'Продолжить'}
        </PageButton>
        <PageButton
          type="button"
          onClick={onCancel}
          pageId={pageId}
          buttonId="email-cancel"
        >
          Назад
        </PageButton>
      </form>
    );
  }

  const isRegister = step === STEPS.REGISTER;

  return (
    <form className="email-auth-form" onSubmit={isRegister ? submitRegister : submitLogin}>
      <h2 className="email-auth-title">{isRegister ? 'Придумай пароль' : 'Введи пароль'}</h2>
      <p className="email-auth-text">{email}</p>
      <PasswordInput
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        // Подсказка менеджерам паролей: новый пароль и существующий
        // подставляются по-разному
        autoComplete={isRegister ? 'new-password' : 'current-password'}
        disabled={isBusy}
        autoFocus
      />
      {isRegister && !error && (
        <p className="email-auth-hint">{PASSWORD_REQUIREMENTS_HINT}</p>
      )}
      {error && <p className="email-auth-error">{error}</p>}
      <PageButton
        type="submit"
        disabled={isBusy || !password}
        pageId={pageId}
        buttonId={isRegister ? 'email-register' : 'email-login'}
      >
        {isBusy ? 'Отправляем...' : (isRegister ? 'Зарегистрироваться' : 'Войти')}
      </PageButton>
      {!isRegister && (
        <PageButton
          type="button"
          onClick={requestPasswordReset}
          disabled={isBusy}
          pageId={pageId}
          buttonId="email-forgot"
        >
          Забыли пароль
        </PageButton>
      )}
      <PageButton
        type="button"
        onClick={goBackToEmail}
        pageId={pageId}
        buttonId="email-back"
      >
        Другая почта
      </PageButton>
    </form>
  );
}

export default EmailAuthForm;
